/**
 * Lead card on the Sales > Leads kanban per design README 5.8.
 *
 * Mono company line, bold contact name, value + Current Action chip on
 * the footer. Step 1 removed drag-to-move-stage: stage now derives from
 * the Current Action set on the card's slide-over, so the card is a plain
 * clickable surface that reflows columns when the action changes.
 */

import { LEAD_ACTION_COLORS, LEAD_ACTION_LABELS, type LeadAction } from '@workspace/shared';

import type { WireLead } from '@/lib/api/sales';

interface LeadCardProps {
  lead: WireLead;
  onClick: (id: string) => void;
}

export function LeadCard({ lead, onClick }: LeadCardProps) {
  const value = parseFloat(lead.potentialValue) > 0 ? formatCurrency(lead.potentialValue) : null;

  return (
    <article
      data-testid="lead-card"
      data-lead-id={lead.id}
      onClick={() => onClick(lead.id)}
      className="group relative cursor-pointer select-none border border-mx-grey-800 bg-mx-card p-mx-3 transition-colors duration-mx-default ease-mx-default hover:border-mx-grey-700"
    >
      <p className="mono-label text-mx-grey-mid">{lead.companyName}</p>
      <h3 className="mt-mx-1 text-mx-body-sm font-medium text-mx-grey-300">
        {lead.contactName ?? '— no contact —'}
      </h3>
      <div className="mt-mx-2 flex items-center justify-between gap-mx-2">
        <span
          className="mono-label truncate px-mx-2 py-[2px] text-mx-mono-sm"
          style={{
            backgroundColor: LEAD_ACTION_COLORS[lead.currentAction as LeadAction]?.bg,
            color: LEAD_ACTION_COLORS[lead.currentAction as LeadAction]?.fg,
          }}
        >
          {LEAD_ACTION_LABELS[lead.currentAction as LeadAction] ?? lead.currentAction}
        </span>
        {value && (
          <span className="tnum font-display text-mx-body-sm font-semibold text-mx-grey-300">
            {value}
          </span>
        )}
      </div>
    </article>
  );
}

function formatCurrency(value: string): string {
  const num = parseFloat(value);
  if (Number.isNaN(num)) return value;
  if (num >= 1_000_000) return `$${(num / 1_000_000).toFixed(2)}M`;
  if (num >= 1_000) return `$${(num / 1_000).toFixed(1)}K`;
  return `$${num.toFixed(0)}`;
}
